<template>
  <div class="container">
    <div class="back-button" v-if="backButton" @click="$emit('back')">
      <div class="icon-button">
        <n-icon icon="arrow-left" size="s" />
      </div>
      <div>{{ backButtonLabel }}</div>
    </div>
    <div
      class="title-row"
      :class="{
        'with-back-button': backButton,
        'has-subtitle': !!subtitle,
        'with-close-button': closable,
        m: titleSize === 'm'
      }"
    >
      <div class="titlebar-title">{{ title }}</div>
      <span v-if="titleSuffix" class="beta-element">{{ titleSuffix }}</span>
    </div>
    <div class="subtitle" v-if="subtitle">{{ subtitle }}</div>
    <div v-if="showMenu" class="menu-button" ref="menuButton" @click="$emit('openMenu', $refs.menuButton)">
      <div class="icon-button">
        <n-icon icon="dots-horizontal" size="s" />
      </div>
    </div>
    <div class="close-button">
      <button
        v-if="closable"
        type="button"
        class="icon-button"
        :aria-label="$t('general.close')"
        @click="$emit('close')"
      >
        <n-icon icon="x" size="s" />
      </button>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator'

@Component
export default class NTitleBar extends Vue {
  @Prop({ default: false, type: Boolean }) backButton!: boolean
  @Prop({ default: false, type: Boolean }) showMenu!: boolean
  @Prop({ default: undefined }) backButtonLabel?: string
  @Prop({ default: '' }) title!: string
  @Prop({ type: String, validator: (prop: string) => ['m', 'l'].includes(prop), default: 'l' })
  private titleSize!: string
  @Prop({ default: '' }) subtitle?: string
  @Prop({ default: '', type: String }) titleSuffix?: string
  @Prop({ default: true, type: Boolean }) closable!: boolean
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.container {
  position: relative;
  display: grid;
  align-items: flex-start;
  grid-column-gap: $notch-space-2x;
  margin-bottom: $notch-space-6x;

  .title-row {
    display: flex;
    align-items: center;
    gap: $notch-space-2x;

    &.with-back-button {
      margin-top: $notch-space-6x;
    }

    &.with-close-button {
      margin-right: $notch-space-6x;
    }

    &.m .titlebar-title {
      @include notch-text-heading-semibold-M;
    }
  }

  .titlebar-title {
    @include notch-text-heading-semibold-L;

    @include mobile {
      @include notch-text-title-strong-M;
    }
  }

  .beta-element {
    @include notch-text-label-light-XS;
    color: $notch-color-semantic-info-800;
    padding: 0 $notch-space-2x;
    border-radius: $notch-radius-10x;
    flex-shrink: 0;
  }

  .subtitle {
    @include notch-text-label-light-M;
    margin-top: $notch-space-1x;

    @include mobile {
      margin-top: $notch-space-2x;
    }
  }

  .back-button {
    @include notch-text-overline-M;
    color: $notch-color-neutral-sand-600;

    display: flex;
    align-items: center;
    justify-content: flex-start;

    cursor: pointer;

    > * + * {
      margin-left: $notch-space-1x;
    }
  }

  .close-button {
    position: absolute;
    top: 0;
    right: 0;
  }

  .menu-button {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }

  .icon-button {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 0;
    border: 0;
    background: none;
    color: inherit;
  }
}
</style>
